import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCreateProject } from '@fluxter/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface ProjectCreateModalProps {
  workspaceSlug: string;
  open: boolean;
  onClose: () => void;
}

export const ProjectCreateModal = ({ workspaceSlug, open, onClose }: ProjectCreateModalProps) => {
  const navigate = useNavigate();
  const create = useCreateProject(workspaceSlug);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() ? description.trim() : undefined,
      });
      const slug = (result as { data?: { attributes?: { slug?: string } } } | undefined)?.data?.attributes?.slug;
      reset();
      onClose();
      if (slug) {
        await navigate({
          to: '/w/$workspaceSlug/p/$projectSlug',
          params: { workspaceSlug, projectSlug: slug },
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create project.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>
            Projects group tasks, notes, and time. You can rename or archive a project later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              type="text"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Website Redesign"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-description">Description (optional)</Label>
            <Textarea
              id="project-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this project about?"
            />
          </div>

          {error ? (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
